import { type ApolloError } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  InputGroup,
  ProgressBar,
} from "@blueprintjs/core";
import {
  GetMediaDocument,
  GetMediaGridDocument,
  useCreateMediaMutation,
} from "@package/ui-gql/index-data-access";
import { last } from "lodash";
import { useState } from "react";

type DialogNewFolderProps = {
  isOpen: boolean;
  selectedParentIdHierarchy: string[];
  onClose: () => void;
  onCreated: (id: string) => void;
  onError: (error: ApolloError) => void;
};

export function DialogNewMediaFolder({
  isOpen,
  selectedParentIdHierarchy,
  onClose,
  onCreated,
  onError,
}: DialogNewFolderProps) {
  const [newFolderName, setNewFolderName] = useState<string>("");

  const [mediaCreate, { error: errorCreate, loading: loadingCreate }] =
    useCreateMediaMutation();

  if (errorCreate) {
    onError(errorCreate);
  }

  return (
    <Dialog
      icon="folder-new"
      isOpen={isOpen}
      onClose={() => onClose()}
      title="Create new Folder"
    >
      <DialogBody>
        <FormGroup label="Folder Name" labelFor="control-create-folder">
          <InputGroup
            disabled={loadingCreate}
            id="control-create-folder"
            value={newFolderName}
            onValueChange={(value) => setNewFolderName(value)}
          />
        </FormGroup>
        {!!loadingCreate && <ProgressBar animate intent="primary" stripes />}
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button
              intent="none"
              disabled={loadingCreate}
              text="Cancel"
              onClick={() => onClose()}
            />
            <Button
              className="ml-2"
              intent="primary"
              text="Create"
              disabled={!newFolderName || loadingCreate}
              onClick={() => {
                mediaCreate({
                  awaitRefetchQueries: true,
                  refetchQueries: [GetMediaGridDocument, GetMediaDocument],
                  variables: {
                    isDir: true,
                    parent: last(selectedParentIdHierarchy),
                    name: newFolderName,
                  },
                  onCompleted: (media) => {
                    onClose();

                    if (media?.data?.id) {
                      onCreated(media.data.id);
                    }
                  },
                });
              }}
            />
          </>
        }
      />
    </Dialog>
  );
}
